// SPDX-License-Identifier: AGPL-3.0-or-later
import type {AgentMachineView, AgentToolInfo} from '@fluxer/schema/src/domains/konvo/Agents';

export type SetupProvider = 'claudeAgent' | 'codex';
export type SetupPlatform = AgentMachineView['platform'];
export type SetupStatus = NonNullable<AgentToolInfo['agents']>[number]['status'];

export const setupAgentName = (provider: SetupProvider) => (provider === 'codex' ? 'Codex' : 'Claude Code');
/** A supplied CLI location stays a literal argument in the user's terminal. */
function setupLocalCommand(
	provider: SetupProvider,
	args: string,
	path?: string,
	platform: SetupPlatform = 'other',
): string {
	const cli = provider === 'codex' ? 'codex' : 'claude';
	if (!path?.trim() || platform === 'other') return `${cli} ${args}`;
	const literal = `'${path.trim().replaceAll("'", platform === 'win32' ? "''" : "'\"'\"'")}'`;
	const runner = /\.[cm]?js$/i.test(path.trim()) ? 'node ' : platform === 'win32' ? '& ' : '';
	return `${runner}${literal} ${args}`;
}
export const setupLoginCommand = (provider: SetupProvider, path?: string, platform?: SetupPlatform) =>
	setupLocalCommand(provider, provider === 'codex' ? 'login' : 'auth login', path, platform);
export const setupStatusCommand = (provider: SetupProvider, path?: string, platform?: SetupPlatform) =>
	setupLocalCommand(provider, provider === 'codex' ? 'login status' : 'auth status --json', path, platform);
export const setupDocs = (provider: SetupProvider) =>
	provider === 'codex' ? 'https://developers.openai.com/codex/cli' : 'https://code.claude.com/docs/en/setup';
export function setupInstallCommand(provider: SetupProvider, platform: SetupPlatform): string | null {
	if (platform === 'other') return null;
	if (provider === 'codex') return 'npm install -g @openai/codex';
	return platform === 'win32'
		? 'irm https://claude.ai/install.ps1 | iex'
		: 'curl -fsSL https://claude.ai/install.sh | bash';
}
export function setupFindCommand(provider: SetupProvider, platform: SetupPlatform): string | null {
	if (platform === 'other') return null;
	const command = provider === 'codex' ? 'codex' : 'claude';
	return platform === 'win32'
		? `Get-Command ${command} -ErrorAction SilentlyContinue | Select-Object -ExpandProperty Source`
		: `command -v ${command}`;
}
export function setupStatus(machine: AgentMachineView, provider: SetupProvider): SetupStatus {
	if (machine.tools.some((tool) => tool.models?.some((model) => model.provider === provider))) return 'ready';
	return (
		machine.tools.flatMap((tool) => tool.agents ?? []).find((agent) => agent.provider === provider)?.status ??
		'not_installed'
	);
}
